import * as d3 from "d3";
import { displayToast, fillColorByDepth, sendAppsignalError, initializeModals } from "../modules/utils";
import screenfull from "screenfull";
import AjaxModal from "../classes/ajax_modal";

class ForecastTree {
  constructor(node) {
    this.element = node;

    // Elements
    this.fullScreenButton = document.querySelector(".js-toggle-fullscreen");
    this.headerElement = document.getElementById("header");
    this.svgWrapper = this.element.querySelector(".js-forecast-tree");
    this.forecastModal = document.querySelector(".js-forecast-modal");
    this.expandIcon = this.element.querySelector(".js-expand-icon");

    // Properties
    this.width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    this.height = this.svgWrapper.clientHeight;
    this.centerX = this.width / 2;
    this.root;
    this.tree;
    this.nodeWidth = 294;
    this.nodeHeight = 246;
    this.scale = 1;
    this.i = 0;
    this.scaledWidth = this.nodeWidth * this.scale;
    this.scaledHeight = this.nodeHeight * this.scale;
    this.initialTransform = d3.zoomIdentity.translate(this.centerX - this.nodeWidth / 2, 48);
    this.allExpanded = false;
    this.minScale = 0.1;
    this.maxScale = 1.5;
    this.initialized = false;
    this.isZooming = false;
    this.cssSimplifiedScope = "g.node > *:not(.node-main):not(.node-hover):not(.node-circle)"; // With this selector we remove complex elements from the nodes
    this.selectOptions = this.element.dataset.selectOptions.split(",");
    this.pgvText = this.element.dataset.pgvText;
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    this.updateTreeTimeout = null;
    this.url = this.element.dataset.url;
    this.minPgvValue = parseInt(this.element.dataset.minPgvValue);
    this.maxPgvValue = parseInt(this.element.dataset.maxPgvValue);
    this.shouldTransitionSimplified = false;
    // Bindings
    this.click = this.click.bind(this);
    this.resize = this.resize.bind(this);

    this.fetchData();
  }

  updateTreeWithNewData() {
    if (!this.initialized) {
      displayToast(this.element.dataset.errorMessage);
      return;
    }

    clearTimeout(this.updateTreeTimeout);

    this.updateTreeTimeout = setTimeout(() => {
      // Since this.root is circular json we need to use a replacer
      this.fetchData({
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(this.root.data, this.getCircularReplacer()),
      });
    }, 50);
  }

  fetchData(options = {}) {
    this.element.classList.add("is-loading", "loading-tree");

    options.retries = 3;
    options.retryDelay = 1000;

    fetch(this.url, options)
      .then((response) => {
        this.response = response;

        if (response.status === 200) {
          return response.json();
        }

        throw new Error("Something went wrong");
      })
      .then((data) => {
        const { html, tree } = data;

        if (tree) {
          // We remove existing nodes
          if (this.svgGroup !== undefined) {
            this.svgGroup.selectAll("*").remove();
          }

          this.buildTree(tree);
        }

        if (html) {
          this.forecastModal.innerHTML = html;

          window.Beyuna.initClasses(this.forecastModal);
          initializeModals(this.forecastModal);
        }

        this.element.classList.remove("is-loading", "loading-tree");
      })
      .catch((e) => {
        console.error(e);
        displayToast(this.element.dataset.errorMessage);
        sendAppsignalError(e, "ForecastTree", {
          response: this.response,
        });
      });
  }

  getCircularReplacer() {
    // Allows a circular JSON object to be stringified

    const seen = new WeakSet();
    return (key, value) => {
      if (key === "openChildren") {
        return;
      }
      if (typeof value === "object" && value !== null) {
        if (seen.has(value)) {
          // we still want to return the value, but we don't want to return the circular reference

          return {
            name: value.name,
            id: value.id,
            QVLevel: value.QVLevel,
            PGV: value.PGV,
            depth: value.depth,
            children: value.children,
          };
        }
        seen.add(value);
      }
      return value;
    };
  }

  buildTree(data) {
    this.i = 0;
    this.root = d3.hierarchy(data, function (d) {
      return d.children;
    });

    this.root.each(function (d) {
      if (d.children) {
        d.data.openChildren = d.children;
      } else {
        d.data.openChildren = null;
      }
    });

    this.root.x0 = 0;
    this.root.y0 = 0;
    this.tree = d3.tree().nodeSize([380, this.width / (this.root.height + 2)]);

    if (!this.initialized) {
      // We add event listeners incase this is the first time we are building the tree
      this.initialize();
      return;
    }

    this.update(this.root, { withoutTransition: true });
  }

  initialize() {
    window.addEventListener("resize", this.resize);

    this.zoom = d3
      .zoom()
      .filter((event) => {
        return this.shouldZoom(event);
      })
      .scaleExtent([this.minScale, this.maxScale]) // min and max zoom
      .on("start", () => {
        this.startZoom = true;
      })
      .on("zoom", (event) => {
        requestAnimationFrame(() => {
          if (this.startZoom) {
            // We only want to zoom when the user is actually zooming. Sometimes the zoom event is triggered without triggering the start event
            this.hasZoomed = true;

            clearTimeout(this.zoomTimeout);

            if (this.shouldTransitionSimplified) {
              // Only remove the complex nodes if we are zooming with more than 4 nodes
              this.svgGroup.selectAll(this.cssSimplifiedScope).remove();
            }
            this.svgGroup.attr("transform", event.transform);

            if (!this.isSafari) {
              // Since this causes significant performance issues on Safari we only do this on other browsers
              this.svgWrapper.style.backgroundPosition = `${event.transform.x}px ${event.transform.y}px`;
              this.svgWrapper.style.transform = `translate3d(0,0,0)`; // We force hardware acceleration
              this.svgWrapper.style.backgroundSize = `${event.transform.k * 26}px`; // We scale the background image (original size is 26px)
            }

            this.zoomTimeout = setTimeout(() => {
              // When idle, we end the zoom
              this.zoom.on("end")();
            }, 500);
          }
        });
      })
      .on("end", () => {
        if (this.startZoom && this.hasZoomed) {
          this.hasZoomed = false;
          this.startZoom = false;

          // update the nodes with the new positions
          this.svgGroup.selectAll("g.node").remove();

          this.update(this.root, { withoutTransition: true });
        }
      });

    this.svg = d3.select(this.svgWrapper).append("svg").attr("width", this.width).attr("height", this.height).call(this.zoom);

    // Right-click to zoom out
    this.svg.on("contextmenu", (event) => {
      event.preventDefault();

      // Prevent zooming when we are at the min scale
      const currentZoomValue = d3.zoomTransform(this.svgGroup.node());
      clearTimeout(this.zoomOutTimeout);

      if (currentZoomValue.k !== this.minScale) {
        this.element.classList.add("loading-tree");

        this.zoomOutTimeout = setTimeout(() => {
          this.element.classList.remove("loading-tree");
        }, 350);

        this.zoomOut();
      }
    });

    this.svgGroup = this.svg.append("g");
    this.svg.call(this.zoom.transform, this.initialTransform);
    this.svgGroup.attr("transform", this.initialTransform.toString());

    window.addEventListener("fullscreenchange", () => {
      this.toggleHeader();

      const svgElement = this.svg.node();

      if (!document.fullscreenElement) {
        svgElement.setAttribute("width", this.width);
        svgElement.setAttribute("height", this.height);
        this.expandIcon.classList = "icon icon-expand";
      } else {
        svgElement.setAttribute("width", "100%");
        svgElement.setAttribute("height", "100%");
        this.expandIcon.classList = "icon icon-shrink";
      }
    });

    this.fullScreenButton.addEventListener("click", () => {
      if (screenfull.isEnabled) {
        screenfull.toggle();
      } else {
        // Fullscreen is unsupported, so we toggle the header instead
        screenfull.isFullscreen = !screenfull.isFullscreen;
        this.toggleHeader();
        window.dispatchEvent(new Event("resize"));
      }
    });

    this.initialized = true;
  }

  toggleHeader() {
    const headerHeight = this.headerElement.offsetHeight;

    if (screenfull.isFullscreen) {
      this.headerElement.style.position = "absolute";
      this.headerElement.dispatchEvent(
        new CustomEvent("header-toggled", {
          detail: {
            state: "down",
            top: headerHeight,
          },
        })
      );
    } else {
      this.headerElement.style.position = "sticky";
      this.headerElement.dispatchEvent(
        new CustomEvent("header-toggled", {
          detail: {
            state: "up",
            top: 0,
          },
        })
      );
    }
  }

  resize() {
    requestAnimationFrame(() => {
      const newHeight = this.svgWrapper.clientHeight;
      const newWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);

      if ((this.height === newHeight && this.width === newWidth) || newHeight <= 0) {
        return;
      }

      this.height = newHeight;
      this.width = newWidth;

      this.svg.attr("width", this.width).attr("height", this.height);

      this.centerX = this.width / 2;

      this.initialTransform = d3.zoomIdentity.translate(this.centerX - this.scaledWidth / 2, 48);
      this.svg.call(this.zoom.transform, this.initialTransform);
      this.svgGroup.attr("transform", this.initialTransform.toString());

      this.svgGroup.selectAll(this.cssSimplifiedScope).remove();
      this.update(this.root, { withoutTransition: true, updateAfterTransition: true });
    });
  }

  zoomOut() {
    this.svg.transition().call(this.zoom.scaleBy, 0.5); // Zoom out by 50%
  }

  buildMainGroup(node, source, simplified = false) {
    const nodeEnter = node
      .enter()
      .append("g")
      .attr("data-id", (d) => d.data.id)
      .attr("class", (d) => {
        return d.data.openChildren ? "node node-opened" : "node";
      })
      .attr("transform", () => `translate(${source.x0},${source.y0})`);

    return !simplified ? nodeEnter : nodeEnter.lower();
  }

  buildNode(node, source, simplified = false) {
    const nodeEnter = this.buildMainGroup(node, source, simplified);

    // Main shape
    nodeEnter
      .append("path")
      .attr("d", "M0 16C0 7.16344 7.16344 0 16 0H278C286.837 0 294 7.16344 294 16V189H0V16Z")
      .attr("fill", "white")
      .attr("stroke", "#EEEEEE")
      .attr("class", "node-main");

    // Bottom shape
    nodeEnter
      .append("path")
      .attr("d", "M0 0H294V56C294 64.8366 286.837 72 278 72H16C7.16345 72 0 64.8366 0 56V0Z")
      .attr("transform", "translate(0, 188)")
      .attr("fill", "white")
      .attr("stroke", "#EEEEEE")
      .attr("class", "node-hover")
      .on("mousedown", (event) => {
        // Prevent zooming when clicking on a node
        event.stopPropagation();
      })
      .on("mouseup", (event, d) => {
        event.stopPropagation();
        this.click(event, d);
      })
      .on("touchstart", (event) => {
        event.stopPropagation();
      })
      .on("touchend", (event, d) => {
        event.stopPropagation();
        this.click(event, d);
      });

    if (simplified) {
      return nodeEnter;
    }

    // Toggle text
    nodeEnter
      .append("text")
      .attr("x", 24)
      .attr("y", 227.784)
      .attr("text-anchor", "start")
      .style("font-family", "Mukta")
      .style("font-size", "14px")
      .style("font-weight", "400")
      .text(this.element.dataset.newDistributorText);

    // Depth shape
    nodeEnter
      .append("rect")
      .attr("x", 23)
      .attr("y", 27.5)
      .attr("width", 25)
      .attr("height", 25)
      .attr("rx", 12)
      .attr("fill", (d) => fillColorByDepth(d.data.depth))
      .attr("stroke", "#ffffff")
      .attr("class", "node-circle");

    // Depth number
    nodeEnter
      .append("text")
      .attr("x", 35.5)
      .attr("y", 45)
      .attr("text-anchor", "middle")
      .style("fill", "white")
      .style("font-family", "Mukta")
      .style("font-size", "16px")
      .attr("class", "depth-text")
      .text((d) => d.data.depth);

    // Toggle button
    nodeEnter
      .append("rect")
      .attr("x", 238.5)
      .attr("y", 209)
      .attr("width", 31)
      .attr("height", 31)
      .attr("rx", 7.5)
      .attr("fill", "white")
      .attr("stroke", "#EEEEEE")
      .attr("class", "toggle-button");

    // Toggle icon
    nodeEnter
      .append("path")
      .attr("d", "M1 5H5M5 5H9M5 5V1M5 5V9")
      .attr("height", 8)
      .attr("width", 8)
      .attr("stroke", "#1F1F1F")
      .attr("stroke-width", "1.5")
      .attr("stroke-linecap", "round")
      .attr("stroke-linejoin", "round")
      .attr("class", "toggle-icon")
      .attr("transform", "translate(249, 219)");

    const nameInputWrapper = nodeEnter.append("foreignObject").attr("x", 22.75).attr("y", 72).attr("width", 246).attr("height", 40);
    const inputDiv = nameInputWrapper.append("xhtml:div"); // For some odd reason we need to wrap the input in a div to avoid the closing tag.
    inputDiv
      .append("input")
      .attr("type", "text")
      .attr("value", (d) => d.data.name)
      .attr("style", "width:246px; height:40px;")
      .on("change", (event, d) => {
        d.data.name = event.target.value;

        this.updateTreeWithNewData();
      })
      .on("mousedown", (event) => {
        // Prevent zooming when clicking on a node
        event.stopPropagation();
      })
      .on("mouseup", (event) => {
        event.stopPropagation();
      })
      .on("touchstart", (event) => {
        event.stopPropagation();
      })
      .on("touchend", (event) => {
        event.stopPropagation();
      });

    const qvLevelWrapper = nodeEnter.append("foreignObject").attr("x", 22.75).attr("y", 124).attr("width", 117).attr("height", 40);
    const selectDiv = qvLevelWrapper.append("xhtml:div"); // For some odd reason we need to wrap the input in a div to avoid the closing tag.

    selectDiv.append("select").attr("style", "width:117px; height:40px; font-size:14px;");

    this.selectOptions.forEach((option) => {
      selectDiv
        .select("select")
        .on("change", (event, d) => {
          d.data.QVLevel = event.target.value;

          this.updateTreeWithNewData();
        })
        .on("mousedown", (event) => {
          event.stopPropagation();
        })
        .on("mouseup", (event) => {
          event.stopPropagation();
        })
        .on("touchstart", (event) => {
          event.stopPropagation();
        })
        .on("touchend", (event) => {
          event.stopPropagation();
        })
        .append("option")
        .attr("value", option)
        .text(option)
        .filter((d) => d.data.QVLevel === option)
        .attr("selected", "selected");
    });

    const pgvWrapper = nodeEnter.append("foreignObject").attr("x", 152.75).attr("y", 124).attr("width", 117).attr("height", 40).attr("class", "input-wrapper");
    const pgvDiv = pgvWrapper.append("xhtml:div");

    pgvDiv
      .append("input")
      .attr("type", "number")
      .attr("value", (d) => d.data.PGV)
      .attr("class", () => {
        return this.isSafari ? "pgv-input safari" : "pgv-input";
      })
      .on("change", (event, d) => {
        if (isNaN(event.target.value)) {
          return;
        }

        if (event.target.value < this.minPgvValue) {
          event.target.value = this.minPgvValue;
        }

        if (event.target.value > this.maxPgvValue) {
          event.target.value = this.maxPgvValue;
        }

        d.data.PGV = event.target.value;

        this.updateTreeWithNewData();
      })
      .on("mousedown", (event) => {
        // Prevent zooming when clicking on a node
        event.stopPropagation();
      })
      .on("mouseup", (event) => {
        event.stopPropagation();
      })
      .on("touchstart", (event) => {
        event.stopPropagation();
      })
      .on("touchend", (event) => {
        event.stopPropagation();
      });

    if (!this.isSafari) {
      // We are unable to show this on Safari without rendering issues.
      pgvDiv.append("div").attr("class", "pgv-wrapper").text(this.pgvText);
    }

    // Delete button unless it's the root node
    nodeEnter
      .append("rect")
      .attr("x", 238.5)
      .attr("y", 24)
      .attr("width", 31)
      .attr("height", 31)
      .attr("rx", 7.5)
      .attr("fill", "#FFFFFF")
      .attr("stroke", "#EEEEEE")
      .attr("class", "destroy-button")
      .on("mousedown", (event) => {
        // Prevent zooming when clicking on a node
        event.stopPropagation();
      })
      .on("mouseup", (event, d) => {
        event.stopPropagation();

        this.showConfirmationModal(d);
      })
      .on("touchstart", (event) => {
        event.stopPropagation();
      })
      .on("touchend", (event, d) => {
        event.stopPropagation();

        this.showConfirmationModal(d);
      });

    const trashIconGroup = nodeEnter.append("g").attr("transform", "translate(248, 33)").attr("fill", "none").attr("class", "destroy-icon");

    trashIconGroup
      .append("path")
      .attr(
        "d",
        "M2 8.33333V6.72152C2 6.0368 1.77792 5.37056 1.36709 4.82278V4.82278C0.803817 4.07176 1.3397 3 2.27848 3H9.72152C10.6603 3 11.1962 4.07176 10.6329 4.82278V4.82278C10.2221 5.37056 10 6.0368 10 6.72152V11.6667C10 12.7712 9.10457 13.6667 8 13.6667H4C2.89543 13.6667 2 12.7712 2 11.6667V10.3333"
      )
      .attr("stroke", "#1F1F1F")
      .attr("stroke-width", "1.5")
      .attr("stroke-linecap", "round")
      .attr("stroke-linejoin", "round");

    trashIconGroup
      .append("path")
      .attr("d", "M7.33325 10.333L7.33325 6.33301")
      .attr("stroke", "#1F1F1F")
      .attr("stroke-width", "1.5")
      .attr("stroke-linecap", "round")
      .attr("stroke-linejoin", "round");

    trashIconGroup
      .append("path")
      .attr("d", "M4.66675 10.333L4.66675 6.33301")
      .attr("stroke", "#1F1F1F")
      .attr("stroke-width", "1.5")
      .attr("stroke-linecap", "round")
      .attr("stroke-linejoin", "round");

    trashIconGroup
      .append("path")
      .attr("d", "M7.75789 1.21038C7.54819 1.0761 7.30028 1 7.03903 1H4.96105C4.6998 1 4.45189 1.0761 4.24219 1.21038")
      .attr("stroke", "#1F1F1F")
      .attr("stroke-width", "1.5")
      .attr("stroke-linecap", "round");

    // pointers.
    const pointerGroupTop = nodeEnter.append("g").attr("transform", "translate(136.5, -11)").attr("class", "pointer-group pointer-group-top");
    pointerGroupTop.append("circle").attr("cx", 10).attr("cy", 10).attr("r", 10).attr("fill", "#EB6209").attr("fill-opacity", "0.16");
    pointerGroupTop.append("circle").attr("cx", 10).attr("cy", 10).attr("r", 6).attr("fill", "white").attr("stroke", "#EB6209").attr("stroke-width", 4);

    const pointerGroupBottom = nodeEnter.append("g").attr("transform", "translate(136.5, 250)").attr("class", "pointer-group pointer-group-bottom");
    pointerGroupBottom.append("circle").attr("cx", 10).attr("cy", 10).attr("r", 10).attr("fill", "#EB6209").attr("fill-opacity", "0.16");
    pointerGroupBottom.append("circle").attr("cx", 10).attr("cy", 10).attr("r", 6).attr("fill", "white").attr("stroke", "#EB6209").attr("stroke-width", 4);

    return nodeEnter;
  }

  update(source, options = { withoutTransition: false, simplifiedTransition: false, updateAfterTransition: false }) {
    this.element.classList.add("loading-tree");

    const treeData = this.tree(this.root);
    const descendantNodes = treeData.descendants();
    const links = treeData.links();

    // Space between levels and between nodes
    descendantNodes.forEach((d) => {
      d.y = d.depth * 339;
      d.x = d.x * 0.8325;
    });

    let existingNodes = this.svgGroup.selectAll("g.node");
    existingNodes = existingNodes.data(descendantNodes, (d) => d.id || (d.id = ++this.i));

    // Remove any exiting nodes
    if (options.withoutTransition) {
      existingNodes.exit().remove();
    } else {
      existingNodes
        .exit()
        .transition()
        .duration(350)
        .attr("transform", () => `translate(${source.x},${source.y})`)
        .remove();
    }

    let nodeUpdate;
    if (options.simplifiedTransition) {
      nodeUpdate = this.buildNode(existingNodes, source, true).merge(existingNodes);
    } else {
      nodeUpdate = this.buildNode(existingNodes, source).merge(existingNodes);
    }

    if (options.withoutTransition) {
      nodeUpdate.attr("transform", (d) => `translate(${d.x},${d.y})`);
      this.element.classList.remove("loading-tree");
    } else {
      nodeUpdate
        .transition()
        .duration(350)
        .attr("transform", (d) => `translate(${d.x},${d.y})`);
    }

    // Update the links
    const link = this.svgGroup.selectAll("path.link").data(links, (d) => d.target.id);

    const linkEnter = link
      .enter()
      .insert("path", "g")
      .attr("class", "link")
      .attr("d", () => this.diagonal({ x: source.x0, y: source.y0 }, { x: source.x0, y: source.y0 }))
      .style("stroke-width", "1px")
      .style("stroke", "#CACACA")
      .style("fill", "none");

    const linkUpdate = linkEnter.merge(link);

    if (options.withoutTransition) {
      linkUpdate.attr("d", (d) => this.diagonal(d.source, d.target));
    } else {
      // Transition back to the parent element position
      linkUpdate
        .transition()
        .duration(350)
        .attr("d", (d) => this.diagonal(d.source, d.target));
    }

    if (options.withoutTransition) {
      link.exit().remove();
    } else {
      // Remove any exiting links
      link
        .exit()
        .transition()
        .duration(350)
        .attr("d", () => this.diagonal({ x: source.x, y: source.y }, { x: source.x, y: source.y }))
        .remove();
    }

    // Store the old positions for transition.
    descendantNodes.forEach((d) => {
      d.x0 = d.x;
      d.y0 = d.y;
    });

    clearTimeout(this.transitionTimeout);
    // We wait for the transitions to finish before re-enabling zoom
    this.transitionTimeout = setTimeout(() => {
      if (options.updateAfterTransition) {
        this.svgGroup.selectAll("g.node").remove();

        this.update(source, { withoutTransition: true });
      } else {
        this.element.classList.remove("loading-tree");
      }
    }, 350);

    this.shouldTransitionSimplified = descendantNodes.length > 4;
    this.element.classList.remove("is-loading");
  }

  deleteNode(node) {
    this.deleteNodeAndChildren(node);
    this.updateTreeWithNewData();
  }

  showConfirmationModal(node) {
    new AjaxModal(this.element);

    window.addEventListener(
      "modal:loaded",
      () => {
        const nameElement = document.querySelector(".js-forecast-distributor-name");
        nameElement.textContent = node.data.name;

        const confirmButton = document.querySelector(".js-forecast-confirm");
        confirmButton.addEventListener("click", this.deleteNode.bind(this, node), { once: true });

        const modal = document.querySelector(".js-modal");
        modal.addEventListener("hide.bs.modal", () => {
          confirmButton.removeEventListener("click", this.deleteNode.bind(this, node));
        });
      },
      { once: true }
    );
  }

  click(event, d) {
    if (event.target.classList.value.includes("destroy-button")) {
      return;
    }

    const newNode = {
      name: this.element.dataset.newDistributorText,
      id: Date.now(),
      QVLevel: "Earth",
      PGV: 0,
      depth: d.depth + 1,
      children: [],
    };

    const newChildParent = this.root.descendants().find((node) => node.data.id === d.data.id);

    if (newChildParent) {
      if (newChildParent.children) {
        newChildParent.data.children.push(newNode);
      } else {
        newChildParent.data.children = [newNode];
      }
    }

    this.root = d3.hierarchy(this.root.data, function (d) {
      return d.children;
    });

    this.root.each(function (d) {
      if (d.children) {
        d.data.openChildren = d.children;
      } else {
        d.data.openChildren = null;
      }
    });

    this.update(d, { withoutTransition: true });

    this.updateTreeWithNewData();
  }

  centerNode(source) {
    // center to the node that was clicked
    const scale = d3.zoomTransform(this.svg.node()).k;
    const scaledWidth = this.nodeWidth * scale;

    const x = -source.x0 * scale + this.centerX - scaledWidth / 2;
    const y = -source.y0 * scale + 48;

    this.svg.transition().duration(350).call(this.zoom.transform, d3.zoomIdentity.translate(x, y).scale(scale));
  }

  diagonal(s, t) {
    const x = s.x + this.scaledWidth / 2; // Adjust start x
    const y = s.y + this.scaledHeight / 1.8; // Adjust start y
    const ex = t.x + this.scaledWidth / 2; // Adjust target x
    const ey = t.y + this.scaledHeight / 2; // Adjust target y

    let xrvs = ex - x < 0 ? -1 : 1;
    let yrvs = ey - y < 0 ? -1 : 1;

    let rdef = 43;
    let r = Math.abs(ex - x) / 2 < rdef ? Math.abs(ex - x) / 2 : rdef;

    r = Math.abs(ey - y) / 2 < r ? Math.abs(ey - y) / 2 : r;

    let h = Math.abs(ey - y) / 2 - r;
    let w = Math.abs(ex - x) - r * 2;
    return `
        M ${x} ${y}
        L ${x} ${y + h * yrvs}
        C  ${x} ${y + h * yrvs + r * yrvs} ${x} ${y + h * yrvs + r * yrvs} ${x + r * xrvs} ${y + h * yrvs + r * yrvs}
        L ${x + w * xrvs + r * xrvs} ${y + h * yrvs + r * yrvs}
        C  ${ex}  ${y + h * yrvs + r * yrvs} ${ex}  ${y + h * yrvs + r * yrvs} ${ex} ${ey - h * yrvs}
        L ${ex} ${ey}
  `;
  }

  deleteNodeAndChildren(node) {
    if (node.parent) {
      // We can't delete the root node

      const parent = node.parent;
      const index = parent.children.indexOf(node);

      parent.children.splice(index, 1);
      parent.data.children.splice(index, 1);

      this.root = d3.hierarchy(this.root.data, function (d) {
        return d.children;
      });

      this.root.each(function (d) {
        if (d.children) {
          d.data.openChildren = d.children;
        } else {
          d.data.openChildren = null;
        }
      });

      this.root.x0 = 0;
      this.root.y0 = 0;
    } else {
      console.warn("Cannot delete root node");
    }

    this.update(this.root, { withoutTransition: true });

    const parentNode = this.svgGroup.selectAll("g.node").filter((d) => d.data.id === node.parent.data.id);
    const parentNodeElement = parentNode._groups[0][0];

    if (parentNodeElement) {
      const rectBounds = parentNodeElement.getBoundingClientRect();
      const svgBounds = this.svgWrapper.getBoundingClientRect();

      const isVisible = rectBounds.top >= svgBounds.top && rectBounds.bottom <= svgBounds.bottom && rectBounds.left >= svgBounds.left && rectBounds.right <= svgBounds.right;

      if (!isVisible && node.data.children.length > 0) {
        // if the parent node is not visible and the deleted node had children, we center the parent node
        this.centerNode(node.parent);
      }
    }
  }

  shouldZoom(event) {
    // Returns whether or not the zoom event should be triggered.
    const currentZoomValue = d3.zoomTransform(this.svgGroup.node());

    if (event.type === "wheel") {
      if ((event.deltaY < 0 && currentZoomValue.k === this.minScale) || (event.deltaY > 0 && currentZoomValue.k === this.maxScale)) {
        return true;
      }

      if (currentZoomValue.k === this.minScale || currentZoomValue.k === this.maxScale) {
        return false;
      }
    }

    if (event.type === "dblclick") {
      clearTimeout(this.dblclickTimeout);

      if (currentZoomValue.k !== this.maxScale) {
        this.element.classList.add("loading-tree");

        this.dblclickTimeout = setTimeout(() => {
          this.element.classList.remove("loading-tree");
        }, 350);

        return true;
      } else {
        return false;
      }
    }
    return true;
  }
}

const forecastElement = document.querySelector('[data-js="forecast-tree"]');
if (forecastElement) {
  new ForecastTree(forecastElement);
}
